import { useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Badge,
  Box,
  ClickAwayListener,
  // Link,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Popper,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material';

// project-imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// assets
//@ts-ignore
import { Gift, Message2, MessageText1, Notification, Setting2, Warning2 } from 'iconsax-react';
import Avatar from 'components/@extended/Avatar';

// types
import { ThemeMode } from 'types/config';
import timeAgo, { getTimeOnly } from 'utils/timeAgo';
import { ScrollY } from 'components/ScrollX';
import { useDataContext } from 'contexts/DataContext';

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const NotificationPage = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const { notifications } = useDataContext();
  const [notificationClicked, setNotificationClicked] = useState<boolean>(false);

  // Check if there are any critical FNO sync overdue notifications
  const hasCriticalNotification = !notificationClicked && notifications?.some(
    (n: any) => n.type === 'fnoSyncOverdue'
  );

  const handleToggle = (e: any) => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    setNotificationClicked(true);
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'secondary.200';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'secondary.100';

  const slicedNotification = notifications.length > 30 ? notifications?.slice(0, 30) : notifications;

  return (
    <Box sx={{ flexShrink: 0, ml: 0.5 }}>
      <IconButton
        color="secondary"
        variant="light"
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        size="large"
        sx={{
          color: hasCriticalNotification ? 'error.main' : 'secondary.main',
          bgcolor: hasCriticalNotification
            ? 'rgba(255, 72, 66, 0.16)'
            : open
              ? iconBackColorOpen
              : iconBackColor,
          p: 1,
          ...(hasCriticalNotification && {
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { boxShadow: '0 0 0 0 rgba(255, 72, 66, 0.4)' },
              '70%': { boxShadow: '0 0 0 8px rgba(255, 72, 66, 0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(255, 72, 66, 0)' }
            }
          })
        }}
      >
        <Badge
          color={hasCriticalNotification ? 'error' : 'success'}
          variant={hasCriticalNotification ? 'standard' : 'dot'}
          badgeContent={
            hasCriticalNotification ? (
              <Warning2 size={10} variant="Bold" />
            ) : undefined
          }
          invisible={!hasCriticalNotification}
          sx={{
            '& .MuiBadge-badge': {
              top: 2,
              right: 4,
              ...(hasCriticalNotification && {
                width: 18,
                height: 18,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              })
            }
          }}
        >
          <Notification variant="Bold" />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? -5 : 0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={matchesXs ? 'top' : 'top-right'} sx={{ overflow: 'hidden' }} in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                borderRadius: 1.5,
                width: '100%',
                minWidth: 285,
                maxWidth: 420,
                [theme.breakpoints.down('md')]: {
                  maxWidth: 285
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">Notifications</Typography>
                  </Stack>
                  <ScrollY>
                    <List
                      component="nav"
                      sx={{
                        '& .MuiListItemButton-root': {
                          p: 1.5,
                          my: 1.5,
                          border: `1px solid ${theme.palette.divider}`,
                          '&:hover': {
                            bgcolor: 'primary.lighter',
                            borderColor: theme.palette.primary.light
                          },
                          '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                        }
                      }}
                    >
                      {slicedNotification?.map((notification: any, i: number) => {
                        const isCritical = notification.type === 'fnoSyncOverdue';
                        return (
                          <ListItemButton
                            key={i}
                            sx={isCritical ? {
                              borderColor: `${theme.palette.error.light} !important`,
                              bgcolor: 'rgba(255, 72, 66, 0.08)'
                            } : {}}
                          >
                            <ListItemAvatar>
                              <Avatar
                                type="filled"
                                color={isCritical ? 'error' : 'primary'}
                              >
                                {isCritical ? (
                                  <Warning2 size={20} variant="Bold" />
                                ) : (
                                  <Message2 size={20} variant="Bold" />
                                )}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={<Typography variant="h6">{notification.message}</Typography>}
                              secondary={timeAgo(notification.timestamp)}
                            />
                            <ListItemSecondaryAction>
                              <Typography variant="caption" noWrap>
                                {getTimeOnly(notification.timestamp)}
                              </Typography>
                            </ListItemSecondaryAction>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </ScrollY>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default NotificationPage;

